/**
 * Patient technique recorder — safe media-stream flow.
 *
 * States: idle → preview → countdown → recording → review → uploading → done
 * (plus error). Camera controls (device/lens selection + genuine hardware
 * zoom) live INSIDE the recorder during Preview. Switching cameras acquires
 * and validates the replacement stream BEFORE releasing the old one, so a
 * failed switch never leaves a black preview. Zoom is shown ONLY when the
 * active track's getCapabilities() actually reports zoom support — no faking.
 * The recorded Blob is held locally and only uploaded when the patient taps
 * "Use video".
 */
import { useEffect, useRef, useState, useCallback } from "react";
import {
  X, Circle, Square, Upload, RefreshCw, CheckCircle2, Timer, SwitchCamera, ZoomIn,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import useModalA11y from "@/lib/useModalA11y";

const MAX_SECONDS = 120;
const DELAYS = [0, 3, 6, 10];

export default function RecordVideoModal({
  exercise,
  onClose,
  onUploaded,
  initialDelay = 3,
  autoOpenOnMount = false,
  contextNote = "",
}) {
  const [phase, setPhase] = useState("idle"); // idle|preview|countdown|recording|review|uploading|done|error
  const [elapsed, setElapsed] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState("");
  const [delay, setDelay] = useState(initialDelay);

  const [devices, setDevices] = useState([]);           // [{deviceId,label}]
  const [currentDeviceId, setCurrentDeviceId] = useState(null);
  const [zoomCaps, setZoomCaps] = useState(null);        // {min,max,step} | null
  const [zoomValue, setZoomValue] = useState(1);
  const [switching, setSwitching] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const blobRef = useRef(null);
  const audioCtxRef = useRef(null);

  const fmt = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  const beep = (freq = 880, ms = 120, gain = 0.15) => {
    try {
      if (!audioCtxRef.current) {
        // eslint-disable-next-line no-undef
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      const osc = ctx.createOscillator(); const g = ctx.createGain();
      osc.frequency.value = freq; osc.type = "sine"; g.gain.value = gain;
      osc.connect(g).connect(ctx.destination);
      osc.start(); setTimeout(() => osc.stop(), ms);
    } catch { /* silent */ }
  };

  const stopStream = (stream) => {
    try { stream?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
  };

  /* Read genuine zoom capability from the active track. */
  const readZoomCaps = useCallback((track) => {
    try {
      const caps = track?.getCapabilities?.() || {};
      if (caps.zoom && typeof caps.zoom.max === "number" && caps.zoom.max > (caps.zoom.min ?? 1)) {
        const settings = track.getSettings?.() || {};
        setZoomCaps({ min: caps.zoom.min ?? 1, max: caps.zoom.max, step: caps.zoom.step || 0.1 });
        setZoomValue(typeof settings.zoom === "number" ? settings.zoom : (caps.zoom.min ?? 1));
        return;
      }
    } catch { /* ignore */ }
    setZoomCaps(null);
    setZoomValue(1);
  }, []);

  /* Attach a stream to the <video> and wait until it's actually playing. */
  const attachAndPlay = useCallback(async (stream) => {
    const v = videoRef.current;
    if (!v) return;
    v.srcObject = stream;
    v.src = "";
    v.muted = true;
    v.playsInline = true;
    v.controls = false;
    try { await v.play?.(); } catch { /* autoplay may resolve late; ok */ }
  }, []);

  /* Validate that the current stream is healthy enough to record. */
  const streamHealthy = () => {
    const t = streamRef.current?.getVideoTracks?.()[0];
    return !!(t && t.readyState === "live" && videoRef.current?.srcObject);
  };

  /* Enumerate labelled video inputs (labels only appear post-permission). */
  const refreshDevices = useCallback(async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      setDevices(all.filter((d) => d.kind === "videoinput")
        .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Camera ${i + 1}` })));
    } catch { /* ignore */ }
  }, []);

  /* Initial camera acquire — MUST run inside a user gesture on iOS Safari. */
  const openCameraFromClick = async () => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setError("Your browser doesn't support in-app recording. Try Safari 14+ or Chrome.");
      setPhase("error");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: true,
      });
      const vt = stream.getVideoTracks()[0];
      if (!vt || vt.readyState !== "live") { stopStream(stream); throw new Error("no-live-track"); }
      streamRef.current = stream;
      setCurrentDeviceId(vt.getSettings?.().deviceId || null);
      await attachAndPlay(stream);
      readZoomCaps(vt);
      await refreshDevices();
      setPhase("preview");
    } catch (e) {
      setError(friendlyError(e));
      setPhase("error");
    }
  };

  /* Safe camera/lens switch: acquire + validate replacement BEFORE releasing
     the current stream. On any failure, the previous working stream is kept. */
  const switchToDevice = async (deviceId) => {
    if (switching || !deviceId || deviceId === currentDeviceId) return;
    setSwitching(true);
    const old = streamRef.current;
    try {
      const next = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: true,
      });
      const vt = next.getVideoTracks()[0];
      if (!vt || vt.readyState !== "live") { stopStream(next); throw new Error("replacement-not-live"); }
      await attachAndPlay(next);
      streamRef.current = next;
      if (old && old !== next) stopStream(old);   // only release the old one now
      setCurrentDeviceId(deviceId);
      readZoomCaps(vt);
    } catch {
      // Preserve the previous working stream + preview.
      if (old && videoRef.current && videoRef.current.srcObject !== old) {
        await attachAndPlay(old);
      }
      toast.error("Couldn't switch camera — keeping the current one.");
    } finally {
      setSwitching(false);
    }
  };

  /* Hardware zoom only — guarded by real capabilities. */
  const applyZoom = async (value) => {
    const track = streamRef.current?.getVideoTracks?.()[0];
    if (!track || !zoomCaps) return;
    const v = Math.min(zoomCaps.max, Math.max(zoomCaps.min, value));
    setZoomValue(v);
    try { await track.applyConstraints({ advanced: [{ zoom: v }] }); }
    catch { /* keep preview; zoom just won't move */ }
  };

  /* ---------- countdown + record ---------- */
  const startCountdown = () => {
    if (!streamHealthy()) {
      toast.error("Camera unavailable. Please try another camera setting.");
      return;
    }
    if (delay === 0) { beginRecording(); return; }
    setCountdown(delay);
    setPhase("countdown");
    beep(880, 120);
    countdownRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(countdownRef.current); countdownRef.current = null;
          beep(1400, 240, 0.2);
          beginRecording();
          return 0;
        }
        beep(880, 120);
        return c - 1;
      });
    }, 1000);
  };

  const cancelCountdown = () => {
    clearInterval(countdownRef.current); countdownRef.current = null;
    setCountdown(0);
    setPhase("preview");
  };

  const beginRecording = () => {
    // Re-validate at the exact moment we start — the stream may have died
    // during the countdown (e.g. device unplugged).
    if (!streamHealthy()) {
      setError("Camera unavailable. Please try another camera setting.");
      setPhase("error");
      return;
    }
    chunksRef.current = [];
    const mime = ["video/webm;codecs=vp9,opus", "video/webm", "video/mp4"]
      .find((m) => window.MediaRecorder.isTypeSupported?.(m)) || "";
    let rec;
    try {
      rec = new MediaRecorder(streamRef.current, mime ? { mimeType: mime } : undefined);
    } catch {
      setError("Recording isn't supported on this browser. Try Safari 14+ or Chrome.");
      setPhase("error");
      return;
    }
    rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
    rec.onstop = () => {
      const type = rec.mimeType || "video/webm";
      const b = new Blob(chunksRef.current, { type });
      blobRef.current = b;
      const u = URL.createObjectURL(b);
      setBlobUrl(u);
      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.src = u;
        videoRef.current.muted = false;
        videoRef.current.controls = true;
      }
      setPhase("review");
    };
    mediaRecorderRef.current = rec;
    rec.start();
    setPhase("recording");
    setElapsed(0);
    timerRef.current = setInterval(() => {
      setElapsed((v) => {
        if (v + 1 >= MAX_SECONDS) { stopRecording(); return MAX_SECONDS; }
        return v + 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    clearInterval(timerRef.current); timerRef.current = null;
    try { mediaRecorderRef.current?.stop(); } catch { /* already stopped */ }
  };

  const retake = async () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    blobRef.current = null;
    setBlobUrl(null);
    setElapsed(0);
    setPhase("preview");
    if (streamRef.current) await attachAndPlay(streamRef.current);
  };

  const upload = async () => {
    if (!blobRef.current) return;
    setPhase("uploading");
    try {
      const fd = new FormData();
      const ext = (mediaRecorderRef.current?.mimeType || "").includes("mp4") ? "mp4" : "webm";
      fd.append("file", blobRef.current, `exercise-${Date.now()}.${ext}`);
      fd.append("exercise_id", exercise?.id || "");
      fd.append("notes", [exercise?.exercise?.name ? `Recorded during: ${exercise.exercise.name}` : null, contextNote || null, notes].filter(Boolean).join(" · "));
      await api.post("/videos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video sent for review");
      setPhase("done");
      onUploaded?.();
      setTimeout(onClose, 900);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
      setPhase("review");
    }
  };

  function friendlyError(e) {
    const n = e?.name || "";
    if (n === "NotAllowedError" || n === "SecurityError") return "Camera access was blocked. Please allow camera access in your browser settings and try again.";
    if (n === "NotFoundError" || n === "OverconstrainedError") return "No usable camera was found. Please check your device camera and try again.";
    if (n === "NotReadableError") return "Your camera is in use by another app. Close it and try again.";
    return "Camera unavailable. Please try another camera setting.";
  }

  // Cleanup on unmount: stop tracks, clear timers, revoke blob, close audio.
  useEffect(() => () => {
    stopStream(streamRef.current); streamRef.current = null;
    clearInterval(timerRef.current); clearInterval(countdownRef.current);
    if (blobRef.current && blobUrl) URL.revokeObjectURL(blobUrl);
    try { audioCtxRef.current?.close(); } catch { /* ignore */ }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (autoOpenOnMount && phase === "idle") openCameraFromClick();
    // eslint-disable-next-line
  }, []);

  const dialogRef = useModalA11y({
    open: true, onClose: () => onClose?.(),
    disableEscape: phase === "recording" || phase === "uploading" || phase === "countdown",
  });

  const showGuide = phase === "preview"; // fades away once recording/countdown begins
  const canSwitch = devices.length > 1;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4" data-testid="record-video-modal">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="record-video-title"
        className="fl-card w-full sm:max-w-2xl h-full sm:h-auto flex flex-col overflow-hidden relative rounded-none sm:rounded-2xl"
      >
        <div className="flex items-center justify-between p-4 border-b shrink-0" style={{ borderColor: "var(--fl-border)" }}>
          <div className="min-w-0">
            <div className="fl-label text-[color:var(--fl-orange)]">Record technique</div>
            <div id="record-video-title" className="fl-heading text-lg sm:text-xl mt-1 truncate">{exercise?.exercise?.name || "Exercise video"}</div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white shrink-0" data-testid="record-video-close" aria-label="Close recording">
            <X size={20} />
          </button>
        </div>

        <div className="relative bg-black overflow-hidden flex-1 min-h-0 flex items-center justify-center">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full sm:h-auto sm:aspect-video object-cover"
            data-testid="record-video-preview"
          />

          {/* Rule-of-thirds grid — preview + countdown + recording */}
          {(phase === "preview" || phase === "countdown" || phase === "recording") && (
            <svg className="pointer-events-none absolute inset-0 w-full h-full opacity-30" data-testid="record-video-grid"
                 viewBox="0 0 300 168" preserveAspectRatio="none">
              <line x1="100" y1="0" x2="100" y2="168" stroke="white" strokeWidth="0.5" />
              <line x1="200" y1="0" x2="200" y2="168" stroke="white" strokeWidth="0.5" />
              <line x1="0" y1="56" x2="300" y2="56" stroke="white" strokeWidth="0.5" />
              <line x1="0" y1="112" x2="300" y2="112" stroke="white" strokeWidth="0.5" />
            </svg>
          )}

          {/* Larger full-body framing guide — Preview only, fades on record */}
          {showGuide && (
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-3" data-testid="record-video-silhouette">
              <div className="rounded-[45%] border-2 border-dashed border-white/40"
                   style={{ width: "42%", height: "78%", maxWidth: 240 }} />
              <div className="absolute bottom-16 text-center text-white/80 text-xs px-6">
                Position your phone so your whole body is visible
              </div>
            </div>
          )}

          {phase === "recording" && (
            <div className="absolute top-3 left-3 flex items-center gap-2 bg-red-600 text-white px-3 py-1.5 rounded-full text-sm font-bold shadow-lg" data-testid="record-video-rec-indicator">
              <Circle size={12} className="animate-pulse fill-white" /> REC {fmt(elapsed)} / {fmt(MAX_SECONDS)}
            </div>
          )}
          {phase === "preview" && (
            <div className="absolute top-3 right-3 bg-black/70 text-white px-3 py-1 rounded-full text-xs">
              Max {fmt(MAX_SECONDS)}
            </div>
          )}
          {phase === "countdown" && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40" data-testid="record-video-countdown">
              <div className="text-white font-bold" style={{ fontSize: 120, lineHeight: 1 }}>{countdown}</div>
              <button className="mt-6 fl-btn-ghost text-white border-white/40" onClick={cancelCountdown} data-testid="record-video-cancel-countdown">
                Cancel
              </button>
            </div>
          )}

          {/* In-recorder camera controls — Preview only */}
          {phase === "preview" && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 w-[90%]">
              {canSwitch && (
                <div className="flex items-center gap-2 bg-black/60 rounded-full px-2 py-1 max-w-full overflow-x-auto no-scrollbar" data-testid="record-video-camera-select">
                  <SwitchCamera size={14} className="text-white/80 shrink-0" />
                  {devices.map((d, i) => (
                    <button
                      key={d.deviceId || i}
                      onClick={() => switchToDevice(d.deviceId)}
                      disabled={switching}
                      className={"text-[11px] px-3 py-1 rounded-full whitespace-nowrap " + (d.deviceId === currentDeviceId
                        ? "bg-white text-black font-semibold" : "text-white/80 hover:text-white")}
                      data-testid={`record-video-camera-${i}`}
                    >
                      {d.label.length > 18 ? d.label.slice(0, 18) + "…" : d.label}
                    </button>
                  ))}
                </div>
              )}
              {zoomCaps && (
                <div className="flex items-center gap-2 bg-black/60 rounded-full px-3 py-1.5 w-full max-w-xs" data-testid="record-video-zoom">
                  <ZoomIn size={14} className="text-white/80 shrink-0" />
                  <input
                    type="range"
                    min={zoomCaps.min} max={zoomCaps.max} step={zoomCaps.step}
                    value={zoomValue}
                    onChange={(e) => applyZoom(Number(e.target.value))}
                    className="flex-1 accent-[color:var(--fl-orange)]"
                    data-testid="record-video-zoom-range"
                    aria-label="Zoom"
                  />
                  <span className="text-[11px] text-white/80 w-9 text-right">{zoomValue.toFixed(1)}×</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-4 space-y-3 shrink-0" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}>
          {phase === "idle" && (
            <>
              <div className="text-sm" style={{ color: "var(--fl-muted)" }}>
                Prop your phone so your whole body is in frame, then open the camera.
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <label className="fl-label flex items-center gap-2 text-xs"><Timer size={12}/> Start delay</label>
                {DELAYS.map((d) => (
                  <button key={d} onClick={() => setDelay(d)}
                    className={"text-xs px-3 py-1 rounded-full border " + (delay === d
                      ? "border-[color:var(--fl-orange)] text-[color:var(--fl-orange)]" : "border-white/10 text-zinc-300")}
                    data-testid={`record-video-delay-${d}`}>
                    {d === 0 ? "Off" : `${d}s`}
                  </button>
                ))}
              </div>
              <div className="flex justify-end">
                <button className="fl-btn" onClick={openCameraFromClick} data-testid="record-video-open-camera">
                  <Circle size={14} className="fill-current" /> Open camera
                </button>
              </div>
            </>
          )}

          {phase === "error" && (
            <div className="space-y-3">
              <div className="text-sm text-red-400" data-testid="record-video-error">{error}</div>
              <div className="flex justify-end">
                <button className="fl-btn" onClick={() => { setPhase("idle"); setError(""); }} data-testid="record-video-retry">
                  <RefreshCw size={14} /> Try again
                </button>
              </div>
            </div>
          )}

          {phase === "preview" && (
            <div className="flex items-center gap-2 flex-wrap">
              <label className="fl-label flex items-center gap-2 text-xs"><Timer size={12}/> Countdown</label>
              {DELAYS.map((d) => (
                <button key={d} onClick={() => setDelay(d)}
                  className={"text-xs px-3 py-1 rounded-full border " + (delay === d
                    ? "border-[color:var(--fl-orange)] text-[color:var(--fl-orange)]" : "border-white/10 text-zinc-300")}
                  data-testid={`record-video-delay-${d}`}>
                  {d === 0 ? "Off" : `${d}s`}
                </button>
              ))}
            </div>
          )}

          {phase === "review" && (
            <div>
              <div className="fl-label mb-1">Notes for your clinician (optional)</div>
              <textarea className="fl-input" rows={2}
                placeholder="Anything specific you want them to check?"
                value={notes} onChange={(e) => setNotes(e.target.value)}
                data-testid="record-video-notes" />
            </div>
          )}

          <div className="flex gap-2 flex-wrap justify-end">
            {phase === "preview" && (
              <button className="fl-btn w-full sm:w-auto justify-center" onClick={startCountdown} data-testid="record-video-start">
                <Circle size={14} className="fill-current" />
                {delay > 0 ? `Start recording (${delay}s)` : "Start recording"}
              </button>
            )}
            {phase === "recording" && (
              <button className="fl-btn w-full sm:w-auto justify-center bg-red-600 hover:bg-red-700 border-red-600 text-white text-base py-3" onClick={stopRecording} data-testid="record-video-stop">
                <Square size={16} className="fill-current" /> Stop recording
              </button>
            )}
            {phase === "review" && (
              <>
                <button className="fl-btn-ghost" onClick={retake} data-testid="record-video-retake">
                  <RefreshCw size={14} /> Retake
                </button>
                <button className="fl-btn" onClick={upload} data-testid="record-video-upload">
                  <Upload size={14} /> Use video
                </button>
              </>
            )}
            {phase === "uploading" && (
              <div className="text-sm" style={{ color: "var(--fl-muted)" }}>Uploading…</div>
            )}
            {phase === "done" && (
              <div className="flex items-center gap-2 text-[color:var(--fl-orange)] text-sm">
                <CheckCircle2 size={16} /> Sent!
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
